import React from 'react'
import { Box, Button } from '@mui/material'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { PaginationItem, usePagination } from '@mui/material'
import clsx from 'clsx'
import { ArrowLeftIcon, ArrowRightIcon } from 'asterX-UI/Icons'

const PaginationRoot = styled('nav')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2)
}))

const PreviousButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(2)
}))

const NextButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2)
}))

const PaginationList = styled('ul')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
})

const Pagination = React.forwardRef(function Pagination(props, ref) {
  const {
    boundaryCount,
    className,
    color = 'primary',
    count,
    isMobile = false,
    defaultPage,
    disabled,
    getItemAriaLabel = defaultGetAriaLabel,
    hideNextButton,
    hidePrevButton,
    onChange,
    page,
    renderItem = item => <PaginationItem {...item} />,
    shape = 'rounded',
    showFirstButton,
    showLastButton,
    siblingCount,
    variant = 'text',
    size = 'medium',
    setLoading,
    ...other
  } = props

  const { items } = usePagination({
    ...props,
    componentName: 'Pagination'
  })

  const { previous, next } = items
    ?.filter(item => item.type === 'previous' || item.type === 'next')
    ?.reduce((obj, cur) => ({ ...obj, [cur.type]: cur }), {})

  const otherItems = items?.filter(item => item.type !== 'previous' && item.type !== 'next')

  return (
    <div>
      <PaginationRoot aria-label="pagination navigation" className={clsx(className)} ref={ref} {...other}>
        <div>
          {isMobile ? (
            <PaginationItem {...previous} variant="outlined" />
          ) : (
            <PreviousButton {...previous} variant="outlined">
              <ArrowLeftIcon />
              Назад
            </PreviousButton>
          )}
        </div>
        {isMobile ? (
          <div>{`Страница ${page || 0} из ${count || 0}`}</div>
        ) : (
          <PaginationList>
            {otherItems.map((item, index) => (
              <Box key={index}>
                {renderItem({
                  ...item,
                  color,
                  'aria-label': getItemAriaLabel(item.type, item.page, item.selected),
                  shape,
                  size,
                  variant
                })}
              </Box>
            ))}
          </PaginationList>
        )}
        <div>
          {isMobile ? (
            <PaginationItem {...next} variant="outlined" />
          ) : (
            <NextButton {...next} variant="outlined">
              Вперед
              <ArrowRightIcon />
            </NextButton>
          )}
        </div>
      </PaginationRoot>
    </div>
  )
})

// @default tags synced with default values from usePagination
function defaultGetAriaLabel(type, page, selected) {
  const validPage = !isNaN(page) && typeof page === 'number' ? page : 'unknown'

  if (type === 'page') {
    return `${selected ? '' : 'Go to '}page ${validPage}`
  }
  return `Go to ${type} page`
}

Pagination.propTypes = {
  boundaryCount: PropTypes.number,
  className: PropTypes.string,
  color: PropTypes.oneOf(['primary', 'info', 'secondary', 'standard']),
  count: PropTypes.number,
  defaultPage: PropTypes.number,
  disabled: PropTypes.bool,
  isMobile: PropTypes.bool,
  getItemAriaLabel: PropTypes.func,
  hideNextButton: PropTypes.bool,
  hidePrevButton: PropTypes.bool,
  onChange: PropTypes.func,
  page: PropTypes.number,
  renderItem: PropTypes.func,
  shape: PropTypes.oneOf(['round', 'rounded']),
  showFirstButton: PropTypes.bool,
  showLastButton: PropTypes.bool,
  siblingCount: PropTypes.number,
  size: PropTypes.oneOf(['large', 'medium', 'small']),
  variant: PropTypes.oneOf(['outlined', 'text'])
}

export default Pagination
